import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Package, ExternalLink, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';
import { formatPrice } from '../utils/formatters';
import { getProductUrl } from '../utils/url-helpers';

/**
 * Side-by-side product comparison table.
 *
 * Shared by the CompareDrawer overlay and the standalone /compare/:ids page so the
 * two comparison surfaces can never drift apart. Renders the product header row
 * (image + name), then price / brand / category / specification / vendor-count rows,
 * highlighting attributes that differ between products.
 *
 * Props:
 *  - products:       comparison products (compareData.products)
 *  - commonSpecs:    spec names shared and equal across products
 *  - diffSpecs:      spec names that differ across products (highlighted)
 *  - onRemoveProduct: optional (id) => void — when provided, each product header
 *                     shows a remove button (used by the drawer). Omit for the
 *                     read-only page, or wire it to URL rewriting there.
 *  - onProductNavigate: optional () => void — invoked when a product name link is
 *                     clicked (e.g. closeDrawer in the overlay).
 */
function CompareTable({
  products = [],
  commonSpecs = [],
  diffSpecs = [],
  onRemoveProduct,
  onProductNavigate
}) {
  const { language, languageConfig } = useLanguage();
  const t = (key) => getTranslation(language, key);

  // Union of every spec name, preserving first-seen order.
  const allSpecs = [...new Set([...commonSpecs, ...diffSpecs])];

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr>
            <th className="text-left p-3 font-medium text-gray-500 w-32">
              {t('attribute')}
            </th>
            {products.map((product) => (
              <th key={product.id} className="p-3 min-w-[150px]">
                <div className="flex flex-col items-center">
                  {/* Product Image */}
                  <div className="relative w-24 h-24 mb-2">
                    {product.images?.[0] ? (
                      <img
                        src={typeof product.images[0] === 'string' ? product.images[0] : product.images[0].url}
                        alt={product.name}
                        width="96"
                        height="96"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-500" aria-hidden="true" />
                      </div>
                    )}
                    {onRemoveProduct && (
                      <button
                        onClick={() => onRemoveProduct(product.id)}
                        className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                        aria-label={t('removeFromCompare')}
                      >
                        <X className="w-4 h-4 text-gray-500" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  {/* Product Name */}
                  <Link
                    to={getProductUrl(product)}
                    onClick={onProductNavigate}
                    className="text-sm font-medium text-gray-900 hover:text-primary-600 text-center line-clamp-2 flex items-center gap-1"
                  >
                    {product.name}
                    <ExternalLink className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                  </Link>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Price Row */}
          <tr className="bg-gray-50">
            <td className="p-3 font-medium text-gray-900">{t('price')}</td>
            {products.map((product) => (
              <td key={product.id} className="p-3 text-center">
                <span className="font-bold text-lg text-gray-900">
                  {formatPrice(
                    product.minPrice || product.price,
                    product.currency || 'CZK',
                    languageConfig.locale
                  )}
                </span>
                {product.maxPrice && product.maxPrice !== product.minPrice && (
                  <span className="text-sm text-gray-500 block">
                    - {formatPrice(product.maxPrice, product.currency || 'CZK', languageConfig.locale)}
                  </span>
                )}
              </td>
            ))}
          </tr>

          {/* Brand Row */}
          <tr>
            <td className="p-3 font-medium text-gray-900">{t('brand')}</td>
            {products.map((product) => (
              <td key={product.id} className="p-3 text-center text-sm">
                {product.brand || '-'}
              </td>
            ))}
          </tr>

          {/* Category Row */}
          <tr className="bg-gray-50">
            <td className="p-3 font-medium text-gray-900">{t('category')}</td>
            {products.map((product) => (
              <td key={product.id} className="p-3 text-center text-sm">
                {product.category || '-'}
              </td>
            ))}
          </tr>

          {/* Specifications */}
          {allSpecs.map((specName, index) => {
            const isDiff = diffSpecs.includes(specName);
            return (
              <tr key={specName} className={index % 2 === 0 ? '' : 'bg-gray-50'}>
                <td className="p-3 font-medium text-gray-900">
                  {specName}
                  {isDiff && (
                    <span className="ml-2 text-xs text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                      {t('differs')}
                    </span>
                  )}
                </td>
                {products.map((product) => {
                  const specValue = product.specifications?.[specName];
                  return (
                    <td
                      key={product.id}
                      className={`p-3 text-center text-sm ${isDiff ? 'font-medium' : 'text-gray-600'}`}
                    >
                      {specValue !== undefined && specValue !== null
                        ? String(specValue)
                        : '-'}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Vendor Count Row */}
          <tr className="bg-gray-50">
            <td className="p-3 font-medium text-gray-900">{t('vendors')}</td>
            {products.map((product) => (
              <td key={product.id} className="p-3 text-center text-sm">
                {product.vendorCount || product.offers?.length || 0}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default memo(CompareTable);
